import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useCart } from '../../hooks/useCart'
import { useBusinessRulesContext } from '../../hooks/useBusinessRulesContext'
import { getImageUrl } from '../../services/cloudinary.service'
import { formatCurrency } from '../../utils/helpers'

function FashionCart() {
  const navigate = useNavigate()
  const { cartItems, updateQuantity, removeFromCart, getSubtotal, getShippingQuote, getFinalTotal } = useCart()
  const { rules } = useBusinessRulesContext()
  const [removeCandidate, setRemoveCandidate] = useState(null)
  const outOfStockPolicy = rules?.outOfStockPolicy || { visibility: 'show', purchaseBehavior: 'block_purchase', inquiryChannel: 'whatsapp', labelText: 'Sin stock' }
  const allowPurchaseWithoutStock = outOfStockPolicy.purchaseBehavior === 'allow_purchase'
  const hasInvalidStockItems = outOfStockPolicy.purchaseBehavior !== 'allow_purchase'
    ? cartItems.some(i => (i?.stock || 0) <= 0 || (i?.quantity || 0) > (i?.stock || 0))
    : false

  const getVariantPieces = useMemo(() => {
    const splitVariantName = (name) => {
      if (typeof name !== 'string') return []
      return name
        .split('|')
        .map(p => p.trim())
        .filter(Boolean)
    }

    const looksLikeHex = (value) => typeof value === 'string' && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim())

    const buildDisplaySku = (sku, selection, fallbackColorLabel) => {
      if (typeof sku !== 'string' || !sku.trim()) return null
      let next = sku.trim()
      const selectionColor = typeof selection?.color === 'string' ? selection.color.trim() : ''
      const selectionColorLabel = typeof selection?.colorLabel === 'string' ? selection.colorLabel.trim() : ''
      const label = selectionColorLabel || fallbackColorLabel || ''
      if (selectionColor && label) next = next.split(selectionColor).join(label)
      if (label && looksLikeHex(next)) next = label
      if (label) next = next.replace(/#([0-9a-f]{3}|[0-9a-f]{6})/gi, label)
      return next
    }

    return (item) => {
      const variant = item?.variant || null
      const selection = variant?.selection && typeof variant.selection === 'object' ? variant.selection : {}
      const nameParts = splitVariantName(variant?.name)

      const selectionColor = typeof selection.color === 'string' ? selection.color.trim() : ''
      const selectionColorLabel = typeof selection.colorLabel === 'string' ? selection.colorLabel.trim() : ''
      const selectionSize = typeof selection.size === 'string' ? selection.size.trim() : ''

      const fallbackColorLabel = (() => {
        const part = nameParts[0] || ''
        if (!part) return ''
        if (looksLikeHex(part)) return ''
        if (part.toLowerCase().startsWith('color:')) return part.replace(/^color:\s*/i, '').trim()
        return part
      })()

      const displayColorLabel = selectionColorLabel || fallbackColorLabel || (selectionColor && !looksLikeHex(selectionColor) ? selectionColor : '')
      const displaySize = selectionSize || (() => {
        const part = nameParts[1] || ''
        if (!part) return ''
        if (part.toLowerCase().startsWith('size:')) return part.replace(/^size:\s*/i, '').trim()
        return part
      })()

      const displayVariant = (() => {
        const parts = []
        if (displayColorLabel) parts.push(displayColorLabel)
        if (displaySize) parts.push(displaySize)
        if (parts.length) return parts.join(' | ')
        if (typeof variant?.name === 'string' && variant.name.trim()) return variant.name.trim()
        return null
      })()

      const displaySku = buildDisplaySku(item?.sku, selection, displayColorLabel)

      return {
        displayVariant,
        displayColorLabel,
        displayColorValue: selectionColor,
        displaySize,
        displaySku
      }
    }
  }, [])

  const subtotal = getSubtotal()
  const shippingQuote = getShippingQuote()
  const total = getFinalTotal()

  if (!cartItems || cartItems.length === 0) {
    return (
      <main className="w-full max-w-[1440px] mx-auto px-6 lg:px-12 py-12">
        <h1 className="text-4xl font-bold tracking-tight mb-8">Tu Carrito</h1>
        <div className="bg-white p-10 rounded-2xl border border-[#eee] text-center">
          <p className="text-sm text-gray-500 mb-6">Tu carrito está vacío.</p>
          <button
            type="button"
            className="bg-[#181211] text-white px-8 py-4 rounded-lg font-bold text-sm tracking-widest uppercase hover:opacity-90 transition-opacity"
            onClick={() => navigate('/productos')}
          >
            Continuar Comprando
          </button>
        </div>
      </main>
    )
  }

  return (
    <main className="w-full max-w-[1440px] mx-auto px-6 lg:px-12 py-12">
      <h1 className="text-4xl font-bold tracking-tight mb-12">Tu Carrito</h1>
      <div className="flex flex-col lg:flex-row gap-16">
        <div className="flex-1 space-y-8">
          {cartItems.map((item) => {
            const img = getImageUrl(item.image, { width: 400, height: 560, crop: 'fill' })
            const variantInfo = getVariantPieces(item)
            const hasStockInfo = item.stock !== null && item.stock !== undefined
            const isOutOfStock = hasStockInfo ? (item.stock || 0) <= 0 : false
            const disableIncrease = !allowPurchaseWithoutStock && hasStockInfo ? (item.quantity || 0) >= (item.stock || 0) : false
            return (
              <div key={item.id} className="flex gap-6 py-6 border-b border-[#e8e6e1] last:border-0">
                <div className="w-32 h-44 bg-[#f4f3f1] rounded-lg overflow-hidden shrink-0">
                  <img alt={item.productName} className="w-full h-full object-cover" src={img} />
                </div>
                <div className="flex flex-1 flex-col justify-between py-1">
                  <div className="flex justify-between items-start gap-6">
                    <div>
                      <h3 className="text-lg font-bold mb-1">{item.productName}</h3>
                      {variantInfo.displayVariant ? (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {variantInfo.displayColorLabel ? (
                            <span className="inline-flex items-center gap-2 rounded-full border border-[#ece7e2] bg-[#f8f6f4] px-3 py-1 text-xs font-semibold text-gray-700">
                              <span className="text-gray-500 font-bold uppercase tracking-widest text-[10px]">Color</span>
                              {variantInfo.displayColorValue && variantInfo.displayColorValue.startsWith('#') ? (
                                <span className="inline-block w-3 h-3 rounded-full border border-black/10" style={{ backgroundColor: variantInfo.displayColorValue }} />
                              ) : null}
                              <span className="text-[#181211]">{variantInfo.displayColorLabel}</span>
                            </span>
                          ) : null}
                          {variantInfo.displaySize ? (
                            <span className="inline-flex items-center gap-2 rounded-full border border-[#ece7e2] bg-[#f8f6f4] px-3 py-1 text-xs font-semibold text-gray-700">
                              <span className="text-gray-500 font-bold uppercase tracking-widest text-[10px]">Talle</span>
                              <span className="text-[#181211]">{variantInfo.displaySize}</span>
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                      {variantInfo.displaySku ? (
                        <p className="text-xs text-gray-400 mt-2">SKU: {variantInfo.displaySku}</p>
                      ) : null}
                      {isOutOfStock && !allowPurchaseWithoutStock ? (
                        <p className="text-xs font-semibold text-red-600 mt-1">{outOfStockPolicy.labelText || 'Sin stock'}</p>
                      ) : null}
                    </div>
                    <span className="text-lg font-semibold">{formatCurrency(item.price || 0)}</span>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center border border-[#e8e6e1] rounded-lg">
                      <button
                        type="button"
                        className="p-2 hover:text-[#c27e70] transition-colors"
                        onClick={() => updateQuantity(item.id, (item.quantity || 1) - 1)}
                        aria-label="Disminuir"
                      >
                        <span className="material-symbols-outlined text-sm">remove</span>
                      </button>
                      <span className="px-4 text-sm font-semibold">{item.quantity}</span>
                      <button
                        type="button"
                        className="p-2 hover:text-[#c27e70] transition-colors"
                        onClick={() => updateQuantity(item.id, (item.quantity || 1) + 1)}
                        aria-label="Aumentar"
                        disabled={disableIncrease}
                      >
                        <span className="material-symbols-outlined text-sm">add</span>
                      </button>
                    </div>
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 rounded-lg border border-[#ece7e2] bg-white px-4 py-2 text-xs font-bold uppercase tracking-widest text-gray-600 hover:border-red-200 hover:text-red-600 hover:bg-red-50 transition-colors"
                      onClick={() => setRemoveCandidate({ id: item.id, name: item.productName })}
                    >
                      <span className="material-symbols-outlined text-base">delete</span>
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <aside className="w-full lg:w-96">
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-[#f1efee]">
            <h2 className="text-xl font-bold mb-8">Resumen del Pedido</h2>
            <div className="space-y-4 mb-8">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Envío</span>
                <span className="text-[#c27e70] font-medium">
                  {shippingQuote.type === 'negotiation' ? 'A acordar' : shippingQuote.type === 'pending' ? 'Calculado en checkout' : shippingQuote.cost === 0 ? 'Gratis' : formatCurrency(shippingQuote.cost)}
                </span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Impuestos</span>
                <span>{formatCurrency(0)}</span>
              </div>
              <div className="pt-4 border-t border-[#f1efee] flex justify-between text-lg font-bold">
                <span>Total</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>

            <div className="space-y-4">
              <button
                type="button"
                className="w-full bg-[#181211] text-white py-4 rounded-lg font-bold text-sm tracking-widest uppercase hover:opacity-90 transition-opacity"
                onClick={() => {
                  if (outOfStockPolicy.purchaseBehavior === 'inquiry_only' && hasInvalidStockItems) {
                    navigate(outOfStockPolicy.inquiryChannel === 'help_page' ? '/ayuda' : '/ayuda#contacto')
                    return
                  }
                  navigate('/checkout')
                }}
                disabled={outOfStockPolicy.purchaseBehavior === 'block_purchase' && hasInvalidStockItems}
              >
                {outOfStockPolicy.purchaseBehavior === 'inquiry_only' && hasInvalidStockItems ? 'Consultar disponibilidad' : 'Finalizar Compra'}
              </button>
              {outOfStockPolicy.purchaseBehavior === 'block_purchase' && hasInvalidStockItems ? (
                <div className="text-xs font-medium text-red-600">
                  Hay productos sin stock en tu carrito. Ajustá cantidades para continuar.
                </div>
              ) : null}
              <Link className="block text-center text-sm font-semibold text-gray-500 hover:text-[#c27e70] transition-colors" to="/productos">
                Continuar Comprando
              </Link>
            </div>

            <div className="mt-8 pt-8 border-t border-[#f1efee]">
              <div className="flex items-center gap-3 text-gray-500 mb-4">
                <span className="material-symbols-outlined text-xl">lock</span>
                <span className="text-xs font-medium">Pago 100% Seguro</span>
              </div>
              <div className="flex items-center gap-3 text-gray-500">
                <span className="material-symbols-outlined text-xl">local_shipping</span>
                <span className="text-xs font-medium">Devoluciones gratuitas en 30 días</span>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {removeCandidate ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center px-6">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setRemoveCandidate(null)}
            aria-label="Cerrar"
          />
          <div className="relative w-full max-w-md rounded-2xl bg-white border border-[#efe9e6] shadow-xl p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold tracking-tight text-[#181211]">Eliminar producto</h3>
                <p className="text-sm text-gray-600 mt-2">
                  ¿Querés eliminar <span className="font-semibold">{removeCandidate.name}</span> del carrito?
                </p>
              </div>
              <button
                type="button"
                className="rounded-lg p-2 text-gray-400 hover:text-gray-600 hover:bg-[#f8f6f4] transition-colors"
                onClick={() => setRemoveCandidate(null)}
                aria-label="Cerrar"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                className="w-full sm:w-auto px-5 py-3 rounded-lg border border-[#e8e6e1] bg-white text-[#181211] font-bold text-xs tracking-widest uppercase hover:bg-[#f8f6f4] transition-colors"
                onClick={() => setRemoveCandidate(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="w-full sm:w-auto px-5 py-3 rounded-lg bg-[#181211] text-white font-bold text-xs tracking-widest uppercase hover:bg-black transition-colors"
                onClick={() => {
                  removeFromCart(removeCandidate.id)
                  setRemoveCandidate(null)
                }}
              >
                Sí, eliminar
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  )
}

export default FashionCart
